import { useEffect, useState } from "react";
import api, { formatMoney, formatDate, urgencyClass } from "@/lib/api";
import { useCompany } from "@/lib/company";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Area, AreaChart, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, Bar, BarChart, Legend,
} from "recharts";
import { TrendUp, TrendDown, Wallet, Receipt, HandCoins, Bank, ArrowsClockwise, EnvelopeSimple, Warning, ShieldWarning } from "@phosphor-icons/react";
import { toast } from "sonner";

const StatCard = ({ label, value, icon: Icon, trend, testid, tone = "default" }) => {
  const toneClasses = {
    default: "text-primary",
    danger: "text-destructive",
    success: "urgency-green",
    warning: "urgency-orange",
  };
  return (
    <Card data-testid={testid} className="border-border">
      <CardContent className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className={`p-2 rounded-md bg-secondary ${toneClasses[tone]}`}>
            <Icon size={22} weight="duotone" />
          </div>
          {trend != null && (
            <div className={`text-xs font-medium ${trend >= 0 ? "urgency-green" : "urgency-red"}`}>
              {trend >= 0 ? "+" : ""}{trend.toFixed(1)}%
            </div>
          )}
        </div>
        <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
        <div className="font-heading font-extrabold text-2xl tabular-nums mt-1">{value}</div>
      </CardContent>
    </Card>
  );
};

export default function DashboardPage() {
  const { selectedCompany, loadCompanies } = useCompany();
  const [summary, setSummary] = useState(null);
  const [cashflow, setCashflow] = useState([]);
  const [seeding, setSeeding] = useState(false);
  const [sending, setSending] = useState(false);

  const cid = selectedCompany === "all" ? "" : selectedCompany;

  const load = async () => {
    const q = cid ? `?company_id=${cid}` : "";
    const [s, c] = await Promise.all([
      api.get(`/dashboard/summary${q}`),
      api.get(`/dashboard/cashflow${q}${q ? "&" : "?"}months=8`),
    ]);
    setSummary(s.data);
    setCashflow(c.data);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [selectedCompany]);

  const runImport = async () => {
    setSeeding(true);
    try {
      await api.post("/import/seed");
      toast.success("Excel verisi yüklendi");
      await loadCompanies();
      await load();
    } catch (e) {
      toast.error("Yükleme başarısız");
    } finally {
      setSeeding(false);
    }
  };

  const sendEmail = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/notifications/send-now");
      toast.success(`E-posta ${data.to} adresine gönderildi (${data.count} kayıt)`);
    } catch (e) {
      const msg = e.response?.data?.detail || "E-posta gönderilemedi";
      toast.error(typeof msg === "string" ? msg : "E-posta gönderilemedi");
    } finally {
      setSending(false);
    }
  };

  if (!summary) return <div className="text-muted-foreground">Yükleniyor…</div>;

  return (
    <div className="space-y-8">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-heading font-extrabold text-2xl sm:text-3xl tracking-tight">Genel Bakış</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Nakit pozisyonunuz, yaklaşan ödemeleriniz ve akış grafiği.
          </p>
        </div>
        <div className="flex gap-2">
        <Button data-testid="reimport-btn" variant="outline" onClick={runImport} disabled={seeding}>
          <ArrowsClockwise size={16} className="mr-2" />
          {seeding ? "Yükleniyor…" : "Excel'den Yeniden Yükle"}
        </Button>
        <Button data-testid="send-email-btn" onClick={sendEmail} disabled={sending}>
          <EnvelopeSimple size={16} className="mr-2" />
          {sending ? "Gönderiliyor…" : "E-posta Bildirimi Gönder"}
        </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testid="stat-payable" label="Ödenecek Çekler (TRY)" tone="danger"
          value={formatMoney(summary.odenecek_cekler.TRY, "TRY")} icon={Receipt} />
        <StatCard testid="stat-receivable" label="Tahsil Edilecek (TRY)" tone="success"
          value={formatMoney(summary.tahsil_edilecek.TRY, "TRY")} icon={HandCoins} />
        <StatCard testid="stat-bank" label="Banka Bakiyesi (TRY)"
          value={formatMoney(summary.banka_bakiye.TRY, "TRY")} icon={Bank} />
        <StatCard testid="stat-net" label="Net Pozisyon (TRY)"
          tone={summary.net_pozisyon.TRY >= 0 ? "success" : "danger"}
          value={formatMoney(summary.net_pozisyon.TRY, "TRY")}
          icon={summary.net_pozisyon.TRY >= 0 ? TrendUp : TrendDown} />
      </div>

      {summary.cari_ay && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard testid="stat-ay-alacak" label={`${summary.cari_ay.ay} Alacak`} tone="success"
            value={formatMoney(summary.cari_ay.alacak_try, "TRY")} icon={HandCoins} />
          <StatCard testid="stat-ay-borc" label={`${summary.cari_ay.ay} Borç`} tone="danger"
            value={formatMoney(summary.cari_ay.borc_try, "TRY")} icon={Receipt} />
          <StatCard testid="stat-odenmesi-gereken" label="30 Gün Ödenecek Toplam" tone="warning"
            value={formatMoney(summary.risk?.odenmesi_gereken_30gun || 0, "TRY")} icon={Warning} />
          <Card data-testid="stat-risk" className="border-border">
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className={`p-2 rounded-md bg-secondary ${
                  summary.risk?.seviye === "Kritik" ? "urgency-red" :
                  summary.risk?.seviye === "Yüksek" ? "urgency-red" :
                  summary.risk?.seviye === "Orta" ? "urgency-orange" :
                  "urgency-green"
                }`}>
                  <ShieldWarning size={22} weight="duotone" />
                </div>
                <Badge className={
                  summary.risk?.seviye === "Kritik" ? "badge-red" :
                  summary.risk?.seviye === "Yüksek" ? "badge-red" :
                  summary.risk?.seviye === "Orta" ? "badge-orange" :
                  summary.risk?.seviye === "Düşük" ? "badge-green" : "badge-green"
                }>{summary.risk?.seviye || "Yok"}</Badge>
              </div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground">Risk Durumu (30 gün)</div>
              <div className="font-heading font-extrabold text-2xl tabular-nums mt-1">
                %{summary.risk?.risk_yuzde || 0}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                Açık: <span className={summary.risk?.acik_30gun > 0 ? "urgency-red font-semibold" : "urgency-green font-semibold"}>
                  {formatMoney(summary.risk?.acik_30gun || 0)}
                </span>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 border-border">
          <CardHeader>
            <CardTitle className="font-heading">Aylık Nakit Akışı (TRY)</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={cashflow}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(40 15% 88%)" />
                <XAxis dataKey="month" fontSize={12} />
                <YAxis fontSize={12} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(v) => formatMoney(v, "TRY")} />
                <Legend />
                <Bar dataKey="in" name="Giren" fill="hsl(140, 40%, 40%)" radius={[4,4,0,0]} />
                <Bar dataKey="out" name="Çıkan" fill="hsl(10, 60%, 45%)" radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="border-border">
          <CardHeader>
            <CardTitle className="font-heading">Pozisyon Dağılımı</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {[
              { label: "Kendi Çeklerim", value: summary.odenecek_cekler.TRY, cls: "badge-red" },
              { label: "Müşteri Çekleri", value: summary.tahsil_edilecek.TRY, cls: "badge-green" },
              { label: "Takastaki Çekler", value: summary.takasta.TRY, cls: "badge-purple" },
              { label: "Ciro Edilen", value: summary.ciro_edilen.TRY, cls: "badge-blue" },
              { label: "Alacaklar", value: summary.alacaklar.TRY, cls: "badge-green" },
              { label: "Giderler", value: summary.giderler.TRY, cls: "badge-orange" },
              { label: "Kredi Bakiyesi", value: summary.kredi_bakiye.TRY, cls: "badge-yellow" },
            ].map((r) => (
              <div key={r.label} className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <span className={`inline-block w-2 h-2 rounded-full ${r.cls}`}></span>
                  <span className="text-muted-foreground">{r.label}</span>
                </div>
                <span className="tabular-nums font-medium">{formatMoney(r.value, "TRY")}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <Card className="border-border">
        <CardHeader className="flex-row items-center justify-between">
          <CardTitle className="font-heading">Yaklaşan 30 Gün Ödemeleri</CardTitle>
          <div className="text-xs text-muted-foreground">{summary.upcoming.length} kayıt</div>
        </CardHeader>
        <CardContent>
          {summary.upcoming.length === 0 ? (
            <div className="text-sm text-muted-foreground py-8 text-center">
              Yaklaşan ödeme bulunmuyor.
            </div>
          ) : (
            <div className="divide-y divide-border">
              {summary.upcoming.slice(0, 12).map((u, i) => (
                <div key={i} data-testid={`upcoming-row-${i}`} className="flex items-center justify-between py-3">
                  <div className="flex items-center gap-3">
                    <Badge className={urgencyClass(u.kalan_gun)}>
                      {u.kalan_gun} gün
                    </Badge>
                    <div>
                      <div className="font-medium text-sm">{u.baslik}</div>
                      <div className="text-xs text-muted-foreground">
                        {u.type} · {formatDate(u.vade_tarihi)}
                      </div>
                    </div>
                  </div>
                  <div className="tabular-nums font-semibold">{formatMoney(u.tutar, u.currency)}</div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
